import React, { useEffect, useRef, useState } from 'react';
import siteAnalyticsService, { PerformanceResponse } from '../../services/siteAnalytics.service';
import { useDateRange, RangeSelector, LoadError, fa, faDecimal, decodePath } from './useDateRange';
import './analytics.css';

const SiteAnalyticsPerformance: React.FC = () => {
  const dr = useDateRange('7d');
  const [data, setData] = useState<PerformanceResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const reqIdRef = useRef(0);

  const load = async () => {
    const reqId = ++reqIdRef.current;
    setLoading(true);
    setErr(null);
    try {
      const r = await siteAnalyticsService.getPerformance(dr.range);
      if (reqId !== reqIdRef.current) return;
      setData(r);
    } catch (e: any) {
      if (reqId !== reqIdRef.current) return;
      setErr(e?.message || 'خطا در دریافت داده');
    } finally {
      if (reqId === reqIdRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dr.range.from, dr.range.to, dr.refreshKey]);

  const lcpClass = (v: number | null) => (v == null ? '' : v <= 2500 ? 'green' : v <= 4000 ? 'orange' : 'red');
  const inpClass = (v: number | null) => (v == null ? '' : v <= 200 ? 'green' : v <= 500 ? 'orange' : 'red');
  const clsClass = (v: number | null) => (v == null ? '' : v <= 0.1 ? 'green' : v <= 0.25 ? 'orange' : 'red');
  const fmtNum = (v: number | null | undefined, unit = 'ms') => (v == null ? '—' : `${fa(v)} ${unit}`);
  // LCP/FCP بر حسب ثانیه — راهنمای آستانه‌ها هم ثانیه است؛ نمایش ms کنارش گیج‌کننده بود
  const fmtSec = (v: number | null | undefined) => (v == null ? '—' : `${faDecimal(v / 1000, 1)} ثانیه`);
  const fmtCls = (v: number | null | undefined) => (v == null ? '—' : faDecimal(v, 3));
  const label = (v: number | null, good: number, ok: number) =>
    v == null ? '—' : v <= good ? 'خوب' : v <= ok ? 'متوسط' : 'ضعیف';
  const goodBadge = (p?: number | null) =>
    typeof p === 'number' ? (
      <span style={{ color: p >= 75 ? 'var(--color-success-dark)' : p >= 50 ? 'var(--color-warning-dark)' : 'var(--color-error-dark)' }}>
        {' '}• {fa(p)}٪ نمونه‌ها خوب
      </span>
    ) : null;
  const fmtDateTime = (ts: string) =>
    new Date(ts).toLocaleString('fa-IR', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });

  const o: any = data?.overall || null;

  return (
    <div className="analytics-page">
      <div className="analytics-header">
        <h2>عملکرد و خطاها (Core Web Vitals)</h2>
        <div className="analytics-controls">
          <RangeSelector {...dr} />
          <button onClick={dr.refresh} disabled={loading}>
            {loading ? 'در حال بارگذاری…' : 'بروزرسانی'}
          </button>
        </div>
      </div>

      {err && <LoadError message={err} onRetry={load} />}
      {loading && !data && <div className="analytics-loading">در حال بارگذاری…</div>}

      {o && o.samples > 0 ? (
        <>
          <div className="analytics-kpi-grid">
            <div className={`analytics-kpi-card ${lcpClass(o.p75Lcp ?? o.avgLcp)}`}>
              <div className="accent" />
              <div className="label">LCP (Largest Contentful Paint) — p75</div>
              <div className="value">{fmtSec(o.p75Lcp ?? o.avgLcp)}</div>
              <span className="change flat">
                {label(o.p75Lcp ?? o.avgLcp, 2500, 4000)} • میانگین {fmtSec(o.avgLcp)} • p95 {fmtSec(o.p95Lcp)}
                {goodBadge(o.goodLcpPct)}
              </span>
            </div>
            <div className={`analytics-kpi-card ${inpClass(o.p75Inp ?? o.avgInp)}`}>
              <div className="accent" />
              <div className="label">INP (Interaction to Next Paint) — p75</div>
              <div className="value">{fmtNum(o.p75Inp ?? o.avgInp)}</div>
              <span className="change flat">
                {label(o.p75Inp ?? o.avgInp, 200, 500)} • میانگین {fmtNum(o.avgInp)}
                {goodBadge(o.goodInpPct)}
              </span>
            </div>
            <div className={`analytics-kpi-card ${clsClass(o.p75Cls ?? o.avgCls)}`}>
              <div className="accent" />
              <div className="label">CLS (Cumulative Layout Shift) — p75</div>
              <div className="value">{fmtCls(o.p75Cls ?? o.avgCls)}</div>
              <span className="change flat">
                {label(o.p75Cls ?? o.avgCls, 0.1, 0.25)} • میانگین {fmtCls(o.avgCls)}
                {goodBadge(o.goodClsPct)}
              </span>
            </div>
            <div className="analytics-kpi-card">
              <div className="accent" />
              <div className="label">TTFB (Time to First Byte)</div>
              <div className="value">{fmtNum(o.avgTtfb)}</div>
            </div>
            <div className="analytics-kpi-card">
              <div className="accent" />
              <div className="label">FCP (First Contentful Paint)</div>
              <div className="value">{fmtNum(o.avgFcp)}</div>
            </div>
            <div className="analytics-kpi-card purple">
              <div className="accent" />
              <div className="label">تعداد نمونه</div>
              <div className="value">{fa(o.samples)}</div>
              <span className="change flat">{fa(data?.samplesTotal || o.samples)} نمونهٔ Web Vitals</span>
            </div>
          </div>

          <div className="analytics-section">
            <div className="analytics-section-header">
              <h3>راهنمای آستانه‌ها</h3>
            </div>
            <table className="analytics-table">
              <thead>
                <tr>
                  <th>متریک</th>
                  <th>خوب</th>
                  <th>متوسط</th>
                  <th>ضعیف</th>
                </tr>
              </thead>
              <tbody>
                <tr><td>LCP</td><td>≤ ۲٫۵ ثانیه</td><td>۲٫۵–۴ ثانیه</td><td>{`> ۴ ثانیه`}</td></tr>
                <tr><td>INP</td><td>≤ ۲۰۰ms</td><td>۲۰۰–۵۰۰ms</td><td>{`> ۵۰۰ms`}</td></tr>
                <tr><td>CLS</td><td>≤ ۰٫۱</td><td>۰٫۱–۰٫۲۵</td><td>{`> ۰٫۲۵`}</td></tr>
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div className="analytics-empty">
          هنوز نمونه‌ی Web Vitals کافی جمع‌آوری نشده. بسته‌ی <code>web-vitals</code> در figano-web نصب است؛
          باید چند کاربر صفحه‌ها را باز کنند و آن را trigger ببندند (LCP فقط بعد از تعامل اولیه/۵ ثانیه گزارش می‌شود).
        </div>
      )}

      <div className="analytics-section">
        <div className="analytics-section-header">
          <h3>کندترین صفحات (بر اساس p75 LCP)</h3>
        </div>
        {!data || data.perPage.length === 0 ? (
          <div className="analytics-empty">نمونه‌ی کافی برای گزارش per-page نیست (حداقل ۳ نمونه در هر مسیر).</div>
        ) : (
          <table className="analytics-table">
            <thead>
              <tr>
                <th>مسیر</th>
                <th>p75 LCP</th>
                <th>p75 INP</th>
                <th>p75 CLS</th>
                <th>نمونه</th>
              </tr>
            </thead>
            <tbody>
              {data.perPage.map((p: any) => (
                <tr key={p.path}>
                  <td style={{ direction: 'ltr', textAlign: 'right', fontSize: 12 }}>{decodePath(p.path)}</td>
                  <td style={{ color: lcpClass(p.p75Lcp ?? p.avgLcp) === 'red' ? 'var(--color-error-dark)' : undefined, fontWeight: 600 }}>
                    {fmtNum(p.p75Lcp ?? p.avgLcp)}
                  </td>
                  <td>{fmtNum(p.p75Inp ?? p.avgInp)}</td>
                  <td>{fmtCls(p.p75Cls ?? p.avgCls)}</td>
                  <td>{fa(p.samples)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* 🆕 پرتکرارترین خطاها — لاگ خام ۱۰۰تایی «خطای اصلی» را گم می‌کرد */}
      {data && data.errorGroups && data.errorGroups.length > 0 && (
        <div className="analytics-section">
          <div className="analytics-section-header">
            <h3>پرتکرارترین خطاهای JS</h3>
          </div>
          <table className="analytics-table">
            <thead>
              <tr>
                <th>پیام</th>
                <th>نوع</th>
                <th>تکرار</th>
                <th>کاربر</th>
                <th>آخرین</th>
              </tr>
            </thead>
            <tbody>
              {data.errorGroups.map((g, i) => (
                <tr key={i}>
                  <td style={{ direction: 'ltr', textAlign: 'right', fontSize: 11, color: 'var(--color-error-dark)', maxWidth: 380, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={g.message}>
                    {g.message || '—'}
                  </td>
                  <td style={{ fontSize: 12 }}>{g.name}</td>
                  <td><strong>{fa(g.count)}</strong></td>
                  <td>{fa(g.visitors)}</td>
                  <td style={{ whiteSpace: 'nowrap', fontSize: 12 }}>{fmtDateTime(g.lastAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="analytics-section">
        <div className="analytics-section-header">
          <h3>خطاهای JS (۱۰۰ مورد اخیر)</h3>
        </div>
        {!data || data.jsErrors.length === 0 ? (
          <div className="analytics-empty">خطایی ثبت نشده 🎉</div>
        ) : (
          <table className="analytics-table">
            <thead>
              <tr>
                <th>زمان</th>
                <th>نوع</th>
                <th>مسیر</th>
                <th>پیام</th>
              </tr>
            </thead>
            <tbody>
              {data.jsErrors.map((e, i) => (
                <tr key={i}>
                  <td style={{ whiteSpace: 'nowrap', fontSize: 12 }}>{fmtDateTime(e.ts)}</td>
                  <td>{e.name}</td>
                  <td style={{ direction: 'ltr', textAlign: 'right', fontSize: 12 }}>{decodePath(e.path) || '-'}</td>
                  <td style={{ fontSize: 11, color: 'var(--color-error-dark)' }}>{e.properties?.message || e.properties?.reason || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default SiteAnalyticsPerformance;
